// Global authority system for role-based access control
export interface Authority {
    id: string;
    name: string;
    description: string;
    category: 'management' | 'academic' | 'financial' | 'operations';
    icon: string;
}

export const AUTHORITIES: Authority[] = [
    // Management Authorities
    { id: 'all', name: 'All Access', description: 'Full access to all features', category: 'management', icon: 'Crown' },
    { id: 'dashboard', name: 'Dashboard', description: 'View dashboard and analytics', category: 'management', icon: 'LayoutDashboard' },
    { id: 'reports', name: 'Reports', description: 'Generate and view reports', category: 'management', icon: 'FileText' },

    // Academic Authorities
    { id: 'students', name: 'Students', description: 'Manage student information', category: 'academic', icon: 'GraduationCap' },
    { id: 'teachers', name: 'Teachers', description: 'Manage teacher information', category: 'academic', icon: 'UserCheck' },
    { id: 'classes', name: 'Classes', description: 'Manage classes and schedules', category: 'academic', icon: 'BookOpen' },
    { id: 'attendance', name: 'Attendance', description: 'Manage attendance records', category: 'academic', icon: 'Calendar' },
    { id: 'grades', name: 'Grades', description: 'Manage grades and assessments', category: 'academic', icon: 'Award' },

    // Financial Authorities
    { id: 'fees', name: 'Fees', description: 'Manage fee collection', category: 'financial', icon: 'DollarSign' },
    { id: 'expenses', name: 'Expenses', description: 'Manage school expenses', category: 'financial', icon: 'TrendingDown' },
    { id: 'petty_cash', name: 'Petty Cash', description: 'Manage petty cash expenses', category: 'financial', icon: 'Coins' },
    { id: 'petty_cash_approve', name: 'Petty Cash Approval', description: 'Approve or reject petty cash expenses', category: 'financial', icon: 'CheckCircle' },
    { id: 'petty_cash_transfer', name: 'Petty Cash Transfer', description: 'Transfer funds into petty cash', category: 'financial', icon: 'ArrowRightLeft' },
    { id: 'donations', name: 'Donations', description: 'Manage donations', category: 'financial', icon: 'Heart' },
    { id: 'finance_ledger', name: 'Finance Ledger', description: 'View financial ledgers', category: 'financial', icon: 'BookOpen' },
    { id: 'finance_accounts', name: 'Finance Accounts', description: 'Create and manage bank/cash accounts', category: 'financial', icon: 'Wallet' },
    { id: 'payroll', name: 'Payroll', description: 'Manage staff payroll', category: 'financial', icon: 'CreditCard' },
    { id: 'revenue', name: 'Revenue', description: 'View revenue reports', category: 'financial', icon: 'TrendingUp' },
    { id: 'revenue_receive', name: 'Receive Revenue', description: 'Record revenue and credit accounts', category: 'financial', icon: 'Plus' },

    // Operations Authorities
    { id: 'staff', name: 'Staff', description: 'Manage staff information', category: 'operations', icon: 'Users' },
    { id: 'inventory', name: 'Inventory', description: 'Manage school inventory', category: 'operations', icon: 'Package' },
    { id: 'transport', name: 'Transport', description: 'Manage transport services', category: 'operations', icon: 'Bus' },
    { id: 'settings', name: 'Settings', description: 'System settings and configuration', category: 'operations', icon: 'Settings' },
];

export const ROLE_TEMPLATES = {
    admin: ['all'],
    hr: ['dashboard', 'staff', 'teachers', 'students', 'reports'],
    accountant: ['dashboard', 'fees', 'expenses', 'payroll', 'revenue', 'reports', 'finance_ledger', 'petty_cash', 'petty_cash_transfer', 'petty_cash_approve', 'finance_accounts', 'revenue_receive'],
    teacher: ['dashboard', 'students', 'classes', 'attendance', 'reports'],
    student: ['dashboard', 'attendance', 'fees'],
    staff: ['dashboard', 'students', 'attendance'],
    principal: ['dashboard', 'students', 'teachers', 'staff', 'classes', 'reports', 'fees', 'revenue'],
    clerk: ['dashboard', 'students', 'fees', 'attendance'],
};

export type UserRole = 'admin' | 'hr' | 'accountant' | 'teacher' | 'student' | 'staff' | 'principal' | 'clerk';

export interface StaffAuthority {
    [key: string]: boolean;
}

export const hasAuthority = (userAuthorities: StaffAuthority, authority: string): boolean => {
    return userAuthorities.all === true || userAuthorities[authority] === true;
};

export const getAuthoritiesByCategory = (authorities: Authority[]) => {
    return authorities.reduce((acc, authority) => {
        if (!acc[authority.category]) {
            acc[authority.category] = [];
        }
        acc[authority.category].push(authority);
        return acc;
    }, {} as Record<string, Authority[]>);
};
